import { memo, useEffect, useRef, useState, type RefObject } from 'react';
import { THROWABLES, type ThrowableId } from '../../theater/catalog';
import './TheaterEffects.css';

export interface ActiveThrow {
  id: string;
  objectType: ThrowableId;
  originX: number;
  originY: number;
  targetX: number;
  targetY: number;
  flight: string;
  impact: string;
  durationMs: number;
  rareEvent?: string | null;
  username: string;
  timestamp: number;
}

const MAX_VISIBLE = 15;

export default function TheaterEffects({
  throws,
  enabled,
  screenRef,
  theaterRef,
}: {
  throws: ActiveThrow[];
  enabled: boolean;
  screenRef: RefObject<HTMLElement | null>;
  theaterRef: RefObject<HTMLElement | null>;
  onDone?: (id: string) => void;
}) {
  const [items, setItems] = useState<ActiveThrow[]>([]);
  const seen = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      seen.current.clear();
      return;
    }

    setItems((prev) => {
      const next = [...prev];
      let changed = false;
      for (const t of throws) {
        if (seen.current.has(t.id)) continue;
        seen.current.add(t.id);
        next.push(t);
        changed = true;
      }
      while (next.length > MAX_VISIBLE) {
        next.shift();
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [throws, enabled]);

  if (!enabled) return null;

  return (
    <div className="tt-effects" aria-hidden>
      {items.map((t) => (
        <ThrowAnim
          key={t.id}
          throwEvent={t}
          screenRef={screenRef}
          theaterRef={theaterRef}
          onComplete={() => {
            setItems((prev) => prev.filter((x) => x.id !== t.id));
          }}
        />
      ))}
    </div>
  );
}

const ThrowAnim = memo(function ThrowAnim({
  throwEvent,
  screenRef,
  theaterRef,
  onComplete,
}: {
  throwEvent: ActiveThrow;
  screenRef: RefObject<HTMLElement | null>;
  theaterRef: RefObject<HTMLElement | null>;
  onComplete: () => void;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const def = THROWABLES[throwEvent.objectType] || THROWABLES.tomato;
  const isNuclear = throwEvent.rareEvent === 'nuclear-tomato';

  useEffect(() => {
    const el = elRef.current;
    const screen = screenRef.current;
    const theater = theaterRef.current;
    if (!el || !screen || !theater) {
      const t = window.setTimeout(() => onCompleteRef.current(), 0);
      return () => window.clearTimeout(t);
    }

    const theaterRect = theater.getBoundingClientRect();
    const screenRect = screen.getBoundingClientRect();
    const seating = theater.querySelector('.tt-seating') as HTMLElement | null;
    const originRect = seating?.getBoundingClientRect() ?? theaterRect;

    // originX/Y are percentages within the seating area (character position)
    const startX = originRect.left - theaterRect.left + (throwEvent.originX / 100) * originRect.width;
    const startY = originRect.top - theaterRect.top + (throwEvent.originY / 100) * originRect.height;
    const endX = screenRect.left - theaterRect.left + throwEvent.targetX * screenRect.width;
    const endY = screenRect.top - theaterRect.top + throwEvent.targetY * screenRect.height;

    const flightMs =
      throwEvent.flight === 'glide' || throwEvent.flight === 'slow-bounce' ? 1600 : 900;
    const start = performance.now();
    let phase: 'fly' | 'impact' = 'fly';
    let raf = 0;
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      onCompleteRef.current();
    };

    // Place immediately so first paint isn't at 0,0
    el.style.transform = `translate(${startX}px, ${startY}px) scale(${def.projectileScale || 1})`;
    el.dataset.phase = 'fly';

    const tick = (now: number) => {
      if (finished) return;
      const elapsed = now - start;

      if (phase === 'fly') {
        const t = Math.min(1, elapsed / flightMs);
        const eased = 1 - (1 - t) * (1 - t);
        const arc =
          throwEvent.flight === 'glide'
            ? Math.sin(t * Math.PI) * 20
            : Math.sin(t * Math.PI) * (throwEvent.flight === 'arc-heavy' ? 90 : 60);
        const x = startX + (endX - startX) * eased;
        const y = startY + (endY - startY) * eased - arc;

        let rotate = 0;
        if (
          throwEvent.flight === 'spin' ||
          throwEvent.flight === 'frisbee' ||
          throwEvent.flight === 'trail-spin'
        ) {
          rotate = t * 720;
        } else if (throwEvent.flight === 'flail') {
          rotate = Math.sin(t * 20) * 40 + t * 360;
        }

        let scale = def.projectileScale || 1;
        if (isNuclear) scale = 1 + t * t * 4;

        el.style.transform = `translate(${x}px, ${y}px) rotate(${rotate}deg) scale(${scale})`;

        if (t >= 1) {
          phase = 'impact';
          el.dataset.phase = 'impact';
          el.dataset.impact = throwEvent.impact;
          if (isNuclear) el.dataset.rare = 'nuclear-tomato';
          el.style.transform = `translate(${endX}px, ${endY}px) scale(1)`;
        }
        raf = requestAnimationFrame(tick);
        return;
      }

      const impactElapsed = elapsed - flightMs;
      const impactDur = isNuclear ? 3200 : Math.max(1600, throwEvent.durationMs - flightMs);
      if (impactElapsed > impactDur) {
        finish();
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      finished = true;
      cancelAnimationFrame(raf);
    };
    // Intentionally only re-run for this throw id, never on parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [throwEvent.id]);

  return (
    <div ref={elRef} className={`tt-projectile tt-projectile--${throwEvent.objectType}`}>
      <span className="tt-projectile-emoji" data-emoji={def.emoji}>
        {def.emoji}
      </span>
      {throwEvent.impact === 'splat' && !isNuclear && (
        <span className="tt-splat-mark" aria-hidden />
      )}
      {isNuclear && <span className="tt-nuclear-banner">NUCLEAR TOMATO</span>}
    </div>
  );
});
